import type { QuoteTemplate } from '@/types/quoteTemplate';
import { ENV } from '@/config/env';

class QuoteTemplateService {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Get all templates for a provider
  async getTemplatesByProvider(providerId: string): Promise<QuoteTemplate[]> {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quote-templates`,
        { headers: this.getAuthHeaders() }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      return [];
    }
  }

  // Get templates by category
  async getTemplatesByCategory(providerId: string, category: string): Promise<QuoteTemplate[]> {
    const templates = await this.getTemplatesByProvider(providerId);
    return templates.filter(t => t.category === category);
  }

  // Get a single template
  async getTemplate(id: string): Promise<QuoteTemplate | null> {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quote-templates/${id}`,
        { headers: this.getAuthHeaders() }
      );
      
      if (!response.ok) {
        return null;
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to fetch template:', error);
      return null;
    }
  }

  // Create new template
  async createTemplate(templateData: Omit<QuoteTemplate, 'id' | 'usageCount' | 'successRate' | 'createdAt' | 'updatedAt'>): Promise<QuoteTemplate> {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quote-templates`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            name: templateData.name,
            category: templateData.category,
            description: templateData.description,
            base_price: templateData.basePrice,
            max_price: templateData.maxPrice,
            time_estimate: templateData.timeEstimate,
            materials_included: templateData.materialsIncluded,
            warranty: templateData.warranty,
            notes: templateData.notes,
            is_active: templateData.isActive
          })
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to create template');
      }
      
      const data = await response.json();
      // Return a basic template object - the list will be refreshed
      return {
        ...templateData,
        id: data.data.id,
        usageCount: 0,
        successRate: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Failed to create template:', error);
      throw error;
    }
  }

  // Update template
  async updateTemplate(id: string, updates: Partial<QuoteTemplate>): Promise<QuoteTemplate | null> {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quote-templates/${id}`,
        {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            name: updates.name,
            category: updates.category,
            description: updates.description,
            base_price: updates.basePrice,
            max_price: updates.maxPrice,
            time_estimate: updates.timeEstimate,
            materials_included: updates.materialsIncluded,
            warranty: updates.warranty,
            notes: updates.notes,
            is_active: updates.isActive
          })
        }
      );
      
      if (!response.ok) {
        return null;
      }
      
      // Fetch the updated template
      return await this.getTemplate(id);
    } catch (error) {
      console.error('Failed to update template:', error);
      return null;
    }
  }

  // Delete template
  async deleteTemplate(id: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quote-templates/${id}`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders()
        }
      );
      
      return response.ok;
    } catch (error) {
      console.error('Failed to delete template:', error);
      return false;
    }
  }

  // Use template (increment usage count)
  async useTemplate(id: string): Promise<void> {
    try {
      await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quote-templates/${id}/use`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ was_accepted: false })
        }
      );
    } catch (error) {
      console.error('Failed to increment usage:', error);
    }
  }

  // Update success rate when quote is accepted/rejected
  async updateTemplateSuccess(id: string, wasAccepted: boolean): Promise<void> {
    try {
      await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quote-templates/${id}/use`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ was_accepted: wasAccepted })
        }
      );
    } catch (error) {
      console.error('Failed to update template success:', error);
    }
  }

  // Get template suggestions based on job keywords
  async suggestTemplates(providerId: string, jobTitle: string, jobDescription: string): Promise<QuoteTemplate[]> {
    const allTemplates = await this.getTemplatesByProvider(providerId);
    const keywords = (jobTitle + ' ' + jobDescription).toLowerCase();
    
    // Simple keyword matching
    const suggestions = allTemplates.filter(template => {
      const templateKeywords = (template.name + ' ' + template.description + ' ' + template.category).toLowerCase();
      
      // Check for common keywords
      const commonWords = ['sink', 'pipe', 'leak', 'electrical', 'outlet', 'wiring', 'hvac', 'heating', 'cooling', 'emergency'];
      return commonWords.some(word => keywords.includes(word) && templateKeywords.includes(word));
    });

    // Sort by success rate and usage count
    return suggestions.sort((a, b) => {
      const scoreA = (a.successRate * 0.7) + (a.usageCount * 0.3);
      const scoreB = (b.successRate * 0.7) + (b.usageCount * 0.3);
      return scoreB - scoreA;
    });
  }

  // Get template statistics
  async getTemplateStats(providerId: string): Promise<{
    totalTemplates: number;
    totalUsage: number;
    averageSuccessRate: number;
    topPerforming: QuoteTemplate | null;
  }> {
    try {
      const response = await fetch(
        `${ENV.API_BASE_URL}/api/v1/auth/providers/quote-templates/stats`,
        { headers: this.getAuthHeaders() }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      
      const data = await response.json();
      return {
        totalTemplates: data.data.total_templates || 0,
        totalUsage: data.data.total_usage || 0,
        averageSuccessRate: data.data.average_success_rate || 0,
        topPerforming: data.data.top_performing ? { name: data.data.top_performing } as QuoteTemplate : null
      };
    } catch (error) {
      console.error('Failed to fetch template stats:', error);
      return {
        totalTemplates: 0,
        totalUsage: 0,
        averageSuccessRate: 0,
        topPerforming: null
      };
    }
  }
}

export const quoteTemplateService = new QuoteTemplateService();